import React, { Suspense, useLayoutEffect } from 'react'
import { Route, Switch, useHistory, withRouter } from 'react-router-dom'

// Lazy-loaded page components — each becomes a separate webpack chunk
const DashboardGovermentPage = React.lazy(() => import('../views/DashboardGovermentPage'))
const DashboardPage = React.lazy(() => import('../views/DashboardPage'))

const AccountPage = React.lazy(() => import('../views/resources/AccountPage'))
const CompanyPage = React.lazy(() => import('../views/resources/CompanyPage'))
const FindNetworkPage = React.lazy(() => import('../views/resources/FindNetworkPage'))
const SearchAccountPage = React.lazy(() => import('../views/resources/SearchAccountPage'))
const GroupChatPage = React.lazy(() => import('../views/resources/GroupChatPage'))
const MyProfilePage = React.lazy(() => import('../views/resources/MyProfilePage'))
const RequestAccountPageForInfluencer = React.lazy(() => import('../views/resources/RequestAccountForInfluencerPage'))
const RequestAccountPage = React.lazy(() => import('../views/resources/RequestAccountPage'))
const RequestInfluencerPage = React.lazy(() => import('../views/resources/RequestInfluencerPage'))
const RequestSystemAccountPage = React.lazy(() => import('../views/resources/RequestSystemAccountPage'))
const RightMenuPage = React.lazy(() => import('../views/resources/RightMenuPage'))
const RolePage = React.lazy(() => import('../views/resources/RolePage'))

const CheckQRDashboard = React.lazy(() => import('../views/medical/CheckQRDashboard'))
const RequestTestingPage = React.lazy(() => import('../views/medical/RequestTestingPage'))
const RequestVaccinationPage = React.lazy(() => import('../views/medical/RequestVaccinationPage'))
const VaccinationTestingInfoPage = React.lazy(() => import('../views/medical/VaccinationTestingInfoPage'))

const BackgroundPostPage = React.lazy(() => import('../views/content/BackgroundPostPage'))
const CommentPage = React.lazy(() => import('../views/content/CommentPage'))
const FanpagePage = React.lazy(() => import('../views/content/FanpagePage'))
const LearningPage = React.lazy(() => import('../views/content/LearningPage'))
const MyPostPage = React.lazy(() => import('../views/content/MyPostPage'))
const MySurveyPage = React.lazy(() => import('../views/content/MySurveyPage'))
const PostContentScreeningPage = React.lazy(() => import('../views/content/PostContentScreeningPage'))
const PostPage = React.lazy(() => import('../views/content/PostPage'))
const ProfilePage = React.lazy(() => import('../views/content/ProfilePage'))
const QuestionPage = React.lazy(() => import('../views/content/QuestionPage'))
const RewardPage = React.lazy(() => import('../views/content/RewardPage'))
const SchedulePostPage = React.lazy(() => import('../views/content/SchedulePostPage'))
const SurveyPage = React.lazy(() => import('../views/content/SurveyPage'))
const TaskSchedulePage = React.lazy(() => import('../views/content/TaskSchedulePage'))

const QuestionReportPage = React.lazy(() => import('../views/report/QuestionReportPage'))
const ReportedAccountPage = React.lazy(() => import('../views/report/ReportedAccountPage'))
const ReportedPostPage = React.lazy(() => import('../views/report/ReportedPostPage'))
const SupportPage = React.lazy(() => import('../views/report/SupportPage'))

const DocumentOrganizationPage = React.lazy(() => import('../views/assessment/DocumentOrganizationPage'))
const MySelfAssessmentInfluencerPage = React.lazy(() => import('../views/assessment/MySelfAssessmentInfluencerPage'))
const MySelfAssessmentPage = React.lazy(() => import('../views/assessment/MySelfAssessmentPage'))
const SelfAssessmentPage = React.lazy(() => import('../views/assessment/SelfAssessmentPage'))
const SelfAssessmentReportPage = React.lazy(() => import('../views/assessment/SelfAssessmentReportPage'))
const ShareSelfAssessmentPage = React.lazy(() => import('../views/assessment/ShareSelfAssessmentPage'))

const HistoryQAPage = React.lazy(() => import('../views/log/HistoryQAPage'))
const HistoryRewardPage = React.lazy(() => import('../views/log/HistoryRewardPage'))
const HistorySurveyPage = React.lazy(() => import('../views/log/HistorySurveyPage'))
const HistoryEventTrackingPage = React.lazy(() => import('../views/log/HistoryEventTrackingPage'))

const ComplaintPage = React.lazy(() => import('../views/complaint'))
const ElearningCenter = React.lazy(() => import('../views/elearning').then(m => ({ default: m.ElearningCenter })))
const GrivancePage = React.lazy(() => import('../views/grievance'))
const AppsConnectPage = React.lazy(() => import('../views/setting/AppsConnectPage'))
const AttributeReportPage = React.lazy(() => import('../views/setting/AttributeReportPage'))
const ChangeCompanyPage = React.lazy(() => import('../views/setting/ChangeCompanyPage'))
const ConfigPage = React.lazy(() => import('../views/setting/ConfigPage'))
const EmotionPage = React.lazy(() => import('../views/setting/EmotionPage'))
const HelpCenterPage = React.lazy(() => import('../views/setting/HelpCenterPage'))
const LanguagePage = React.lazy(() => import('../views/setting/LanguagePage'))
const LocationPage = React.lazy(() => import('../views/setting/LocationPage'))
const MetadataPage = React.lazy(() => import('../views/setting/MetadataPage'))
const ReasonPage = React.lazy(() => import('../views/setting/ReasonPage'))
const SubMetadataPage = React.lazy(() => import('../views/setting/SubMetadataPage'))

const NotFoundPage = React.lazy(() => import('../views/NotFoundPage'))
const ScheduleAssessmentPage = React.lazy(() => import('../views/schedule-assessment/ScheduleAssessmentPage'))
const NewSurveyPage = React.lazy(() => import('../views/survey/NewSurveyPage'))

const ChatPage = React.lazy(() => import('../views/Chat/ChatPage'))
const CaseAssessmentPage = React.lazy(() => import('../views/assessment/case/CaseAssessmentPage'))
const NewCaseAssessmentPage = React.lazy(() => import('../views/assessment/case/NewCaseAssessmentPage'))
const CategoryQuestionPage = React.lazy(() => import('../views/question-bank/CategoryQuestionPage'))
const QuestionBankPage = React.lazy(() => import('../views/question-bank/QuestionBankPage'))
const QuestionBankReportPage = React.lazy(() => import('../views/question-bank/QuestionBankReportPage'))

const GopyBalancePage = React.lazy(() => import('../views/power-dashboard/GopyBalancePage'))
const PowerDashboardPage = React.lazy(() => import('../views/power-dashboard/PowerDashboardPage'))

const MyFanpagePage = React.lazy(() => import('../views/content/MyFanpagePage'))
const BuyGopiesPage = React.lazy(() => import('../views/payment/BuyGopiesPage'))
const PaymentManagementPage = React.lazy(() => import('../views/payment/PaymentManagementPage'))
const PaymentResultPage = React.lazy(() => import('../views/payment/PaymentResultPage'))

const InsightChartPage = React.lazy(() => import('../views/content/InsightChartPage'))
const EventPage = React.lazy(() => import('../views/event/EventPage'))
const CreateBillPage = React.lazy(() => import('../views/payment/CreateBillPage'))
const UserStoragePage = React.lazy(() => import('../views/setting/UserStoragePage'))
const MonitoringPage = React.lazy(() => import('../views/monitoring/MonitoringPage'))
const AssociationManagementPage = React.lazy(() => import('../views/association-data/AssociationManagementPage'))
const SearchResultsPage = React.lazy(() => import('../views/search-results/SearchResultsPage'))
const ESGResult = React.lazy(() => import('../../components/ESGResult'))
const ESGReportPage = React.lazy(() => import('../views/report/ESGReportPage'))


const Routes = (props) => {
    const history = useHistory()
    const ACCOUNT_TYPE = localStorage.getItem(window.CONSTANT.ACCOUNT_TYPE)
    const packageId = localStorage.getItem(window.CONSTANT.PACKAGE_ID)

    useLayoutEffect(() => {
        setTimeout(() => {
            checkRolePath()
        }, 100)
        // eslint-disable-next-line
    }, [])

    const checkRolePath = () => {
        try {
            if (ACCOUNT_TYPE === window.CONSTANT.SUPER_ADMIN && !packageId) {
                return true
            }

            const currentPath = props.location.pathname
            const pathNotFollowDataRole = ['/new-survey', '/schedule-assessment', '/new-case', '/payment-result']
            if (pathNotFollowDataRole.includes(currentPath)) {
                return true
            }

            const data = JSON.parse(localStorage.getItem(window.CONSTANT.DATA_ROLE))
            let check = false
            for (let i = 0; i < data.length; i++) {
                const element = data[i]
                if (element.action === currentPath.replace('/', '')) {
                    check = true
                    break
                }
                if (element.children && element.children.length > 0) {
                    for (let j = 0; j < element.children.length; j++) {
                        const child = element.children[j]
                        if (child.action === currentPath.replace('/', '')) {
                            check = true
                            break
                        }
                    }
                }
            }
            if (!check && !currentPath.includes('/my-profile') && !currentPath.includes('/profile')) {
                history.push('/404')
            }
        } catch (error) {
            console.log('Error function Routes.checkRolePath')
        }
    }

    if (typeof window === 'undefined') {
        return null
    }
    return (
        <Suspense fallback={<div className="text-center p-5"><i className="fas fa-spinner fa-spin fa-2x"></i></div>}>
        <Switch>
            <Route path="/dashboard" component={DashboardPage} />
            <Route path="/dashboard-goverment" component={DashboardGovermentPage} />
            <Route path="/power-dashboard" component={PowerDashboardPage} />
            <Route path="/gopy-balance" component={GopyBalancePage} />
            <Route path="/create-bill" component={CreateBillPage} />
            <Route path="/buy-gopies" component={BuyGopiesPage} />
            <Route path="/payment-result" component={PaymentResultPage} />
            <Route path="/payment-management" component={PaymentManagementPage} />
            <Route path="/my-profile" component={MyProfilePage} />
            <Route path="/company" component={CompanyPage} />
            <Route path="/users" component={AccountPage} />
            <Route path="/role" component={RolePage} />
            <Route path="/right-menu" component={RightMenuPage} />
            <Route path="/group-chat" component={GroupChatPage} />
            <Route path="/request-account" component={RequestAccountPage} />
            <Route path="/request-influencer" component={RequestInfluencerPage} />
            <Route path="/request-system-account" component={RequestSystemAccountPage} />
            <Route path="/request-account-influencer" component={RequestAccountPageForInfluencer} />
            <Route path="/find-network" component={FindNetworkPage} />
            <Route path="/search-account" component={SearchAccountPage} />
            <Route path="/esg-result" component={ESGResult} />

            <Route path="/check-qr-dashboard" component={CheckQRDashboard} />
            <Route path="/vaccination-request" component={RequestVaccinationPage} />
            <Route path="/testing-request" component={RequestTestingPage} />
            <Route path="/vaccination-testing-info" component={VaccinationTestingInfoPage} />

            <Route path="/question" component={QuestionPage} />
            <Route path="/reward" component={RewardPage} />
            <Route path="/newfeed" component={PostPage} />
            <Route path="/comment" component={CommentPage} />
            <Route path="/learning" component={LearningPage} />
            <Route path="/learning-center" component={ElearningCenter} />
            <Route path="/survey" component={SurveyPage} />
            <Route path="/my-post" component={MyPostPage} />
            <Route path="/profile" component={ProfilePage} />
            <Route path="/schedule-post" component={SchedulePostPage} />
            <Route path="/task-schedule" component={TaskSchedulePage} />
            <Route path="/my-survey" component={MySurveyPage} />
            <Route path="/fanpage" component={FanpagePage} />
            <Route path="/event" component={EventPage} />
            <Route path="/post-content-screening" component={PostContentScreeningPage} />
            <Route path="/background-post" component={BackgroundPostPage} />
            <Route path="/my-fanpage" component={MyFanpagePage} />

            <Route path="/reported-user" component={ReportedAccountPage} />
            <Route path="/reported-post" component={ReportedPostPage} />
            <Route path="/support" component={SupportPage} />
            <Route path="/survey-report" component={QuestionReportPage} />
            <Route path="/esg-report" component={ESGReportPage} />
            <Route path="/new-survey" component={NewSurveyPage} />

            <Route path="/self-assessment-report" component={SelfAssessmentReportPage} />
            <Route path="/self-assessment" component={SelfAssessmentPage} />
            <Route path="/my-self-assessment" component={MySelfAssessmentPage} />
            <Route path="/my-self-assessment-influencer" component={MySelfAssessmentInfluencerPage} />
            <Route path="/shared-self-assessment" component={ShareSelfAssessmentPage} />
            <Route path={'/schedule-assessment'} component={ScheduleAssessmentPage} />
            <Route path="/case-assessment" component={CaseAssessmentPage} />
            <Route path="/new-case" component={NewCaseAssessmentPage} />
            <Route path="/document-organization" component={DocumentOrganizationPage} />

            <Route path="/history-reward" component={HistoryRewardPage} />
            <Route path="/history-qa" component={HistoryQAPage} />
            <Route path="/history-survey" component={HistorySurveyPage} />
            <Route path="/history-event-tracking" component={HistoryEventTrackingPage} />

            <Route path="/metadata" component={MetadataPage} />
            <Route path="/sub-metadata" component={SubMetadataPage} />
            <Route path="/language" component={LanguagePage} />
            <Route path="/emotion" component={EmotionPage} />
            <Route path="/location" component={LocationPage} />
            <Route path="/attribute-report" component={AttributeReportPage} />
            <Route path="/change-company" component={ChangeCompanyPage} />
            <Route path="/reason" component={ReasonPage} />
            <Route path="/help-center" component={HelpCenterPage} />
            <Route path="/config" component={ConfigPage} />
            <Route path="/apps-connect" component={AppsConnectPage} />

            <Route path="/grievance" component={GrivancePage} />
            <Route path="/complaint" component={ComplaintPage} />
            <Route path="/chat" component={ChatPage} />

            <Route path="/question-category" component={CategoryQuestionPage} />
            <Route path="/question-bank" component={QuestionBankPage} />
            <Route path="/question-bank-report" component={QuestionBankReportPage} />
            <Route path="/data-insight" component={InsightChartPage} />
            <Route path="/user-storage" component={UserStoragePage} />
            <Route path="/monitoring" component={MonitoringPage} />
            <Route path="/search" component={SearchResultsPage} />

            <Route path="/association" component={AssociationManagementPage} />

            <Route path="*" exact={true} component={NotFoundPage} />
            <Route exact path="/404" component={NotFoundPage} />
        </Switch>
        </Suspense>
    )
}

export default withRouter(Routes)
